import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { RobotInfo } from '../types';
import { request } from '../api/request';

@customElement('device-list-view')
export class DeviceListView extends LitElement {
    static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
    }

    .title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #102a43;
      margin: 0;
    }

    .btn-primary {
      background-color: #2684ff;
      color: white;
      border: none;
      padding: 0.6rem 1.2rem;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .btn-primary:hover {
      background-color: #005cc5;
    }

    /* Table Styles */
    .table-container {
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
      overflow-x: auto;
      max-height: min(70vh, 720px);
      overflow-y: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      text-align: left;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 1;
    }

    th, td {
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #e1e4e8;
    }

    th {
      background-color: #f8f9fa;
      color: #495057;
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    td {
      color: #333;
      font-size: 0.95rem;
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:hover {
      background-color: #f8f9fa;
    }

    .status-badge {
      display: inline-block;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    .status-badge.online {
      background-color: #d1fae5;
      color: #065f46;
    }

    .status-badge.offline {
      background-color: #fee2e2;
      color: #991b1b;
    }
    
    .status-badge.error {
      background-color: #fef3c7;
      color: #92400e;
    }
    
    .status-badge.unknown {
      background-color: #e2e8f0;
      color: #475569;
    }

    .status-badge.access-on {
      background-color: #dbeafe;
      color: #1d4ed8;
    }

    .status-badge.access-off {
      background-color: #f1f5f9;
      color: #b91c1c;
    }

    .btn-text {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #2563eb;
      padding: 0.25rem 0.35rem;
      border-radius: 4px;
    }

    .btn-text:hover {
      background: #eff6ff;
      color: #1d4ed8;
    }

    /* Actions */
    .actions {
      display: flex;
      gap: 0.5rem;
    }

    .btn-icon {
      background: none;
      border: none;
      cursor: pointer;
      color: #64748b;
      padding: 0.25rem;
      border-radius: 4px;
      transition: all 0.2s;
    }

    .btn-icon:hover {
      background-color: #f1f5f9;
      color: #0f172a;
    }

    .btn-icon.danger:hover {
      color: #ef4444;
      background-color: #fee2e2;
    }

    .empty-state {
      padding: 3rem;
      text-align: center;
      color: #64748b;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 1rem;
      flex-wrap: wrap;
    }

    .pagination .meta {
      font-size: 0.875rem;
      color: #64748b;
      margin-right: auto;
    }

    .pagination button {
      background: white;
      border: 1px solid #e1e4e8;
      color: #334e68;
      padding: 0.4rem 0.85rem;
      border-radius: 6px;
      font-size: 0.875rem;
      cursor: pointer;
    }

    .pagination button:hover:not(:disabled) {
      background: #f8f9fa;
      border-color: #2684ff;
      color: #2684ff;
    }

    .pagination button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .page-size {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.875rem;
      color: #64748b;
    }

    .page-size select {
      border: 1px solid #e1e4e8;
      border-radius: 6px;
      padding: 0.3rem 0.5rem;
      font-size: 0.875rem;
    }
  `;

    @state()
    private devices: RobotInfo[] = [];

    @state()
    private loading = true;

    @state()
    private currentPage = 1;

    @state()
    private pageSize = 10;

    @state()
    private total = 0;

    connectedCallback() {
        super.connectedCallback();
        this.fetchDevices();
    }

    /** 新增成功后应回到第 1 页（新账号按创建时间倒序在首页） */
    public async refreshAfterSave() {
        this.currentPage = 1;
        await this.fetchDevices();
    }

    public async fetchDevices() {
        this.loading = true;
        try {
            const qs = new URLSearchParams({
                page: String(this.currentPage),
                page_size: String(this.pageSize),
            });
            // envelope：保留 total/page，避免 request 默认只解 data 导致无法分页
            const response = await request<{
                data: RobotInfo[];
                page?: number;
                page_size?: number;
                total?: number;
            }>(`/api/v1/devices?${qs.toString()}`, { envelope: true });

            const list = Array.isArray(response) ? response : (response?.data || []);
            const total = Array.isArray(response)
                ? response.length
                : (typeof response?.total === 'number' ? response.total : list.length);

            console.log('[device-list] fetch', {
                page: this.currentPage,
                pageSize: this.pageSize,
                total,
                dataLen: list.length,
            });

            this.devices = list;
            this.total = total;
        } catch (err) {
            console.error('Failed to fetch devices', err);
        } finally {
            this.loading = false;
        }
    }

    private get totalPages(): number {
        return Math.max(1, Math.ceil(this.total / this.pageSize) || 1);
    }

    private goPage(page: number) {
        if (page < 1 || page > this.totalPages || page === this.currentPage || this.loading) return;
        this.currentPage = page;
        void this.fetchDevices();
    }

    private onPageSizeChange(e: Event) {
        const v = Number((e.target as HTMLSelectElement).value);
        if (!Number.isFinite(v) || v < 1) return;
        this.pageSize = v;
        this.currentPage = 1;
        void this.fetchDevices();
    }

    private async handleDelete(sn: string) {
        if (!confirm(`确定要删除设备 ${sn} 吗？`)) return;

        try {
            await request(`/api/v1/devices/${sn}`, { method: 'DELETE' });
            // 删光当前页时回退一页
            if (this.devices.length <= 1 && this.currentPage > 1) {
                this.currentPage -= 1;
            }
            this.fetchDevices();
        } catch (err) {
            // 错误已经在 request 里 alert 过了
        }
    }

    private openCreateModal() {
        this.dispatchEvent(new CustomEvent('open-device-modal', {
            detail: { mode: 'create', device: null },
            bubbles: true,
            composed: true
        }));
    }

    private async openEditModal(device: RobotInfo) {
        let latest: RobotInfo = device;
        try {
            // 使用单条详情接口，避免列表缓存/格式差异导致编辑弹窗回填串值
            const detailRes = await request<any>(`/api/v1/devices/${encodeURIComponent(device.sn)}`);
            console.log("🔍 [UI回显调试] 后端返回的原始数据:", detailRes?.data ?? detailRes);
            latest = (detailRes?.data ?? detailRes) as RobotInfo;
        } catch (err) {
            console.warn('Failed to fetch latest device detail for edit modal, fallback to row data.', err);
        }
        this.dispatchEvent(new CustomEvent('open-device-modal', {
            detail: { mode: 'edit', device: latest },
            bubbles: true,
            composed: true
        }));
    }

    private async toggleStatus(item: RobotInfo) {
        const nextStatus = item.status === 0 ? 1 : 0;
        const verb = nextStatus === 1 ? '启用' : '停用';
        if (!confirm(`确定要【${verb}】该应用接入吗？`)) return;

        const payload = { ...item, status: nextStatus };
        try {
            await request(`/api/v1/devices/${encodeURIComponent(item.sn)}`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            await this.fetchDevices();
        } catch (err) {
            console.error('toggleStatus failed', err);
        }
    }

    private isAccessEnabled(device: RobotInfo): boolean {
        return device.status !== 0;
    }

    render() {
        return html`
      <div class="toolbar">
        <h2 class="title">应用列表</h2>
        <button class="btn-primary" @click=${this.openCreateModal}>+ 新增应用</button>
      </div>

      <div class="table-container">
        ${this.loading ? html`<div class="empty-state">数据加载中...</div>` : this.renderTable()}
      </div>

      ${this.loading ? '' : this.renderPagination()}
    `;
    }

    private renderPagination() {
        if (this.total === 0 && this.devices.length === 0) return html``;
        return html`
      <div class="pagination">
        <span class="meta">共 ${this.total} 条 · 第 ${this.currentPage} / ${this.totalPages} 页</span>
        <label class="page-size">
          每页
          <select .value=${String(this.pageSize)} @change=${this.onPageSizeChange}>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </label>
        <button
          type="button"
          ?disabled=${this.currentPage <= 1 || this.loading}
          @click=${() => this.goPage(this.currentPage - 1)}
        >上一页</button>
        <button
          type="button"
          ?disabled=${this.currentPage >= this.totalPages || this.loading}
          @click=${() => this.goPage(this.currentPage + 1)}
        >下一页</button>
      </div>
    `;
    }

    private renderTable() {
        if (this.devices.length === 0) {
            return html`<div class="empty-state">当前租户名下暂无设备</div>`;
        }

        return html`
      <table>
        <thead>
          <tr>
            <th>认证账号 (Username / Client ID)</th>
            <th>所属公司</th>
            <th>所属数据空间 (Tenant ID)</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          ${this.devices.map(device => html`
            <tr>
              <td><strong>${device.sn}</strong></td>
              <td>${(device as any).company_name || '-'}</td>
              <td><code>${device.tenant_id || (device as any).api_key || '-'}</code></td>
              <td>
                 <span
                   class="status-badge ${this.isAccessEnabled(device) ? 'access-on' : 'access-off'}"
                 >
                   ${this.isAccessEnabled(device) ? '已启用' : '已停用'}
                 </span>
              </td>
              <td>${device.created_at ? new Date(device.created_at).toLocaleString() : '-'}</td>
              <td>
                <div class="actions">
                  <button
                    type="button"
                    class="btn-text"
                    @click=${() => this.toggleStatus(device)}
                  >
                    ${this.isAccessEnabled(device) ? '停用' : '启用'}
                  </button>
                  <button class="btn-icon" title="编辑" @click=${() => this.openEditModal(device)}>✎</button>
                  <button class="btn-icon danger" title="删除" @click=${() => this.handleDelete(device.sn)}>🗑️</button>
                </div>
              </td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'device-list-view': DeviceListView;
    }
}
